import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import './LuckyDice.css';

// Contract ABIs - MATCHED TO YOUR ACTUAL CONTRACT
const LUCKYDICE_ABI = [
  // Game functions
  "function rollDice(uint8 betSum, uint256 betAmount) external",
  "function getGameStats() external view returns (uint256 totalGames, uint256 totalBets, uint256 totalPayoutsAmount, uint256 houseProfit, uint256 companyTotal, uint256 routerTotal)",
  "function getRecentGames(uint256 count) external view returns (tuple(address player, uint256 betAmount, uint8 betSum, uint8 dice1, uint8 dice2, uint8 actualSum, uint256 grossPayout, uint256 netPayout, uint256 timestamp, bool won)[])",
  "function getGameConfig() external view returns (tuple(uint256 noWinChance, uint256[11] sumChances, uint256[11] sumPayouts))",
  "function getSumInfo(uint8 sum) external view returns (uint256 chance, uint256 payout, string memory name)",
  "function getDividendInfo() external view returns (address dividendContractAddress, bool isDividendEnabled)",
  "event GamePlayed(address indexed player, uint256 betAmount, uint8 betSum, uint8 dice1, uint8 dice2, uint8 actualSum, uint256 grossPayout, uint256 netPayout, bool won)",
  
  // Admin functions - READ FUNCTIONS
  "function owner() external view returns (address)",
  "function admin() external view returns (address)",
  "function paused() external view returns (bool)",
  "function houseBalance() external view returns (uint256)",
  "function minBet() external view returns (uint256)",
  "function maxBet() external view returns (uint256)",
  "function getNoWinChance() external view returns (uint256)",
  
  // Admin functions - WRITE FUNCTIONS
  "function pauseGame() external",
  "function unpauseGame() external", 
  "function updateGameConfig(tuple(uint256 noWinChance, uint256[11] sumChances, uint256[11] sumPayouts) newConfig) external",
  "function updateSumChances(uint256[11] newChances) external",
  "function updateNoWinChance(uint256 newNoWinChance) external",
  "function updateSumPayouts(uint256[11] newPayouts) external",
  "function updateSingleSum(uint8 sumIndex, uint256 newChance, uint256 newPayout) external",
  "function setBetLimits(uint256 minBetAmount, uint256 maxBetAmount) external",
  "function fundHouse(uint256 amount) external",
  "function withdrawHouse(uint256 amount) external",
  "function emergencyWithdrawAll() external",
  "function toggleDividend(bool enabled) external",
  
  // Utility functions
  "function getEffectiveMultiplier() external view returns (uint256)",
  "function calculateHouseEdge() external view returns (uint256)"
];

const BLB_TOKEN_ABI = [
  "function balanceOf(address owner) external view returns (uint256)",
  "function allowance(address owner, address spender) external view returns (uint256)",
  "function approve(address spender, uint256 amount) external returns (bool)",
  "function transfer(address to, uint256 amount) external returns (bool)"
];

// Contract Addresses
const CONTRACT_ADDRESSES = {
  LUCKYDICE: "0x61E3ac3351d0E1D6D28f46834f4B6C3BcdaD8D4F", // Replace with actual address
  BLB_TOKEN: "0x9015e335f28cbad84EB255d5205CDb51B08A1B70"
};

// 🎵 EPIC GAMING SOUND SYSTEM 🎵
const playSound = (type, volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const createTone = (frequency, duration, waveType = 'sine') => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
      oscillator.type = waveType;
      
      gainNode.gain.setValueAtTime(volume, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    };

    const createComplexSound = (notes, duration) => {
      notes.forEach((note, index) => {
        setTimeout(() => {
          createTone(note.freq, note.duration || duration, note.wave || 'sine');
        }, note.delay || index * 50);
      });
    };

    switch (type) {
      case 'diceRoll':
        createComplexSound([
          { freq: 200, duration: 1.5, wave: 'square', delay: 0 },
          { freq: 300, duration: 1.3, wave: 'triangle', delay: 100 },
          { freq: 400, duration: 1.1, wave: 'sawtooth', delay: 200 },
          { freq: 500, duration: 0.9, wave: 'sine', delay: 300 }
        ], 1.2);
        break;
        
      case 'epicWin':
        createComplexSound([
          { freq: 659, duration: 0.5, wave: 'triangle', delay: 0 },
          { freq: 784, duration: 0.5, wave: 'triangle', delay: 200 },
          { freq: 988, duration: 0.6, wave: 'sine', delay: 400 },
          { freq: 1319, duration: 0.8, wave: 'sine', delay: 600 },
          { freq: 1976, duration: 1.0, wave: 'sine', delay: 800 }
        ], 0.4);
        break;
        
      case 'bigWin':
        createComplexSound([
          { freq: 523, duration: 0.4, wave: 'triangle', delay: 0 },
          { freq: 659, duration: 0.4, wave: 'triangle', delay: 150 },
          { freq: 784, duration: 0.5, wave: 'sine', delay: 300 },
          { freq: 1047, duration: 0.6, wave: 'sine', delay: 450 }
        ], 0.3);
        break;
        
      case 'smallWin':
        createComplexSound([
          { freq: 440, duration: 0.3, wave: 'triangle', delay: 0 },
          { freq: 523, duration: 0.3, wave: 'sine', delay: 100 },
          { freq: 659, duration: 0.4, wave: 'sine', delay: 200 }
        ], 0.2);
        break;
        
      case 'lose':
        createComplexSound([
          { freq: 400, duration: 0.4, wave: 'triangle', delay: 0 },
          { freq: 300, duration: 0.5, wave: 'sawtooth', delay: 200 },
          { freq: 200, duration: 0.6, wave: 'sawtooth', delay: 400 }
        ], 0.3);
        break;
        
      case 'buttonHover':
        createTone(600, 0.1, 'sine');
        break;
        
      case 'buttonClick':
        createComplexSound([
          { freq: 800, duration: 0.05, wave: 'square', delay: 0 },
          { freq: 1000, duration: 0.05, wave: 'sine', delay: 20 }
        ], 0.05);
        break;
        
      case 'diceStop':
        createComplexSound([
          { freq: 400, duration: 0.2, wave: 'square', delay: 0 },
          { freq: 600, duration: 0.15, wave: 'triangle', delay: 50 }
        ], 0.1);
        break;
        
      case 'sumSelect':
        createComplexSound([
          { freq: 500, duration: 0.1, wave: 'triangle', delay: 0 },
          { freq: 700, duration: 0.15, wave: 'sine', delay: 50 }
        ], 0.1);
        break;
        
      case 'epicStart':
        createComplexSound([
          { freq: 300, duration: 0.3, wave: 'sawtooth', delay: 0 },
          { freq: 500, duration: 0.3, wave: 'triangle', delay: 150 },
          { freq: 800, duration: 0.4, wave: 'sine', delay: 300 },
          { freq: 1200, duration: 0.5, wave: 'sine', delay: 450 }
        ], 0.3);
        break;
        
      default:
        createTone(440, 0.1);
    }
  } catch (error) {
    console.log('Audio not available:', error);
  }
};

const LuckyDice = ({ walletData, onBack }) => {
  const [gameState, setGameState] = useState({
    betAmount: '1',
    selectedSum: null,
    isRolling: false,
    isApproving: false,
    lastResult: null,
    balance: '0',
    allowance: '0',
    playerLevel: 1,
    experience: 0,
    totalWins: 0,
    showRollPopup: false,
    dice: [1, 1] // Default dice values
  });

  const [gameStats, setGameStats] = useState({
    totalGames: 0,
    totalBets: '0',
    totalPayouts: '0',
    houseEdge: '95.0',
    playersOnline: 0
  });

  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminData, setAdminData] = useState({
    houseBalance: '0',
    isPaused: false,
    minBet: '1',
    maxBet: '1000000',
    noWinChance: 500,
    dividendEnabled: false,
    gameConfig: {
      noWinChance: 500,
      sumChances: [278, 556, 833, 1111, 1389, 1667, 1389, 1111, 833, 556, 278],
      sumPayouts: [3000, 1500, 1000, 800, 600, 400, 600, 800, 1000, 1500, 3000]
    }
  });
  const [adminForm, setAdminForm] = useState({
    minBet: '1',
    maxBet: '1000000',
    fundAmount: '',
    withdrawAmount: '',
    noWinChance: '5.0',
    dividendEnabled: false,
    // Sum chances and payouts (arrays)
    sumChances: ['2.78', '5.56', '8.33', '11.11', '13.89', '16.67', '13.89', '11.11', '8.33', '5.56', '2.78'],
    sumPayouts: ['30.0', '15.0', '10.0', '8.0', '6.0', '4.0', '6.0', '8.0', '10.0', '15.0', '30.0']
  });
  const [adminLoading, setAdminLoading] = useState(false);

  const [gameHistory, setGameHistory] = useState([]);
  const [animationState, setAnimationState] = useState('idle');
  const [rollResult, setRollResult] = useState(null);
  const [particles, setParticles] = useState([]);
  const [connected, setConnected] = useState(!!walletData?.account);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [contracts, setContracts] = useState({
    luckyDice: null,
    blbToken: null
  });

  const diceRefs = [useRef(null), useRef(null)];
  
  // Sum names for display
  const sumNames = [
    'Snake Eyes (2)', 'Three (3)', 'Four (4)', 'Five (5)', 'Six (6)',
    'Lucky Seven (7)', 'Eight (8)', 'Nine (9)', 'Ten (10)', 'Eleven (11)', 'Boxcars (12)'
  ];

  const playGameSound = (type, volume = 0.3) => {
    if (soundEnabled) {
      playSound(type, volume);
    }
  };

  // Check if current wallet is admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (walletData?.account && contracts.luckyDice) {
        try {
          const [owner, admin] = await Promise.all([
            contracts.luckyDice.owner().catch(() => null),
            contracts.luckyDice.admin().catch(() => null)
          ]);
          
          const isOwner = owner && owner.toLowerCase() === walletData.account.toLowerCase();
          const isAdminRole = admin && admin.toLowerCase() === walletData.account.toLowerCase();
          const hasAdminAccess = isOwner || isAdminRole;
          
          setIsAdmin(hasAdminAccess);
          
          if (hasAdminAccess) {
            loadAdminData();
          }
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      }
    };

    if (contracts.luckyDice) {
      checkAdminStatus();
    }
  }, [walletData?.account, contracts.luckyDice]);

  // Initialize contracts
  useEffect(() => {
    if (walletData?.provider && walletData?.account) {
      initializeContracts();
    } else {
      setContracts({ luckyDice: null, blbToken: null });
    }
  }, [walletData?.provider, walletData?.account]);

  const initializeContracts = async () => {
    try {
      const signer = walletData.provider.getSigner();
      
      const luckyDiceContract = new ethers.Contract(
        CONTRACT_ADDRESSES.LUCKYDICE,
        LUCKYDICE_ABI,
        signer
      );
      
      const blbTokenContract = new ethers.Contract(
        CONTRACT_ADDRESSES.BLB_TOKEN,
        BLB_TOKEN_ABI,
        signer
      );

      setContracts({
        luckyDice: luckyDiceContract,
        blbToken: blbTokenContract
      });

      setTimeout(() => {
        loadGameData(luckyDiceContract);
        loadBalance(blbTokenContract, luckyDiceContract);
      }, 500);

    } catch (error) {
      console.error('Error initializing contracts:', error);
      setContracts({ luckyDice: null, blbToken: null });
    }
  };

  // Load admin data
  const loadAdminData = async (contract = contracts.luckyDice) => {
    try {
      if (!contract) return;

      const [houseBalance, isPaused, minBet, maxBet, noWinChance, config, dividendInfo] = await Promise.all([
        contract.houseBalance().catch(() => ethers.BigNumber.from(0)),
        contract.paused().catch(() => false),
        contract.minBet().catch(() => ethers.utils.parseEther('1')),
        contract.maxBet().catch(() => ethers.utils.parseEther('1000000')),
        contract.getNoWinChance().catch(() => 500),
        contract.getGameConfig().catch(() => ({
          noWinChance: 500,
          sumChances: [278, 556, 833, 1111, 1389, 1667, 1389, 1111, 833, 556, 278],
          sumPayouts: [3000, 1500, 1000, 800, 600, 400, 600, 800, 1000, 1500, 3000]
        })),
        contract.getDividendInfo().catch(() => [ethers.constants.AddressZero, false])
      ]);

      const adminInfo = {
        houseBalance: ethers.utils.formatEther(houseBalance),
        isPaused,
        minBet: ethers.utils.formatEther(minBet),
        maxBet: ethers.utils.formatEther(maxBet),
        noWinChance: parseInt(noWinChance),
        dividendEnabled: dividendInfo[1],
        gameConfig: {
          noWinChance: parseInt(config.noWinChance),
          sumChances: config.sumChances.map(c => parseInt(c)),
          sumPayouts: config.sumPayouts.map(p => parseInt(p))
        }
      };

      setAdminData(adminInfo);
      setAdminForm({
        minBet: adminInfo.minBet,
        maxBet: adminInfo.maxBet,
        fundAmount: '',
        withdrawAmount: '',
        noWinChance: (adminInfo.gameConfig.noWinChance / 100).toString(),
        dividendEnabled: adminInfo.dividendEnabled,
        sumChances: adminInfo.gameConfig.sumChances.map(c => (c / 100).toString()),
        sumPayouts: adminInfo.gameConfig.sumPayouts.map(p => (p / 100).toString())
      });

    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // Particle effects for wins
  useEffect(() => {
    if (animationState === 'result' && rollResult?.won) {
      const particleCount = rollResult.grossPayout >= 1000 ? 25 : rollResult.grossPayout >= 100 ? 15 : 10;
      const newParticles = Array.from({ length: particleCount }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 5 + 2,
        life: 100,
        velocity: {
          x: (Math.random() - 0.5) * 3,
          y: Math.random() * -2 - 1
        }
      }));
      setParticles(newParticles);
      
      const interval = setInterval(() => {
        setParticles(prev => prev.map(p => ({ 
          ...p, 
          life: p.life - 2,
          x: p.x + p.velocity.x,
          y: p.y + p.velocity.y
        })).filter(p => p.life > 0));
      }, 50);
      
      setTimeout(() => clearInterval(interval), 4000);
    }
  }, [animationState, rollResult]);

  // Connection state
  useEffect(() => {
    if (walletData?.account) {
      setConnected(true);
    }
  }, [walletData]);

  // Load game data from contract
  const loadGameData = async (contract = contracts.luckyDice) => {
    try {
      if (!contract) {
        setGameStats({
          totalGames: 0,
          totalBets: '0',
          totalPayouts: '0',
          houseEdge: '95.0',
          playersOnline: Math.floor(Math.random() * 200) + 50
        });
        return;
      }

      let stats, recentGames, houseEdge;

      try {
        stats = await contract.getGameStats();
      } catch (e) {
        stats = {
          totalGames: ethers.BigNumber.from(0),
          totalBets: ethers.BigNumber.from(0),
          totalPayoutsAmount: ethers.BigNumber.from(0)
        };
      }

      try {
        recentGames = await contract.getRecentGames(5);
      } catch (e) {
        recentGames = [];
      }

      try {
        houseEdge = await contract.calculateHouseEdge();
      } catch (e) {
        houseEdge = ethers.BigNumber.from(9500);
      }

      setGameStats({
        totalGames: parseInt(stats.totalGames.toString()),
        totalBets: ethers.utils.formatEther(stats.totalBets),
        totalPayouts: ethers.utils.formatEther(stats.totalPayoutsAmount),
        houseEdge: (parseInt(houseEdge.toString()) / 100).toString(),
        playersOnline: Math.floor(Math.random() * 200) + 50
      });

      if (recentGames && recentGames.length > 0) {
        const formattedHistory = recentGames.slice(0, 5).map(game => ({
          betSum: parseInt(game.betSum),
          dice1: parseInt(game.dice1),
          dice2: parseInt(game.dice2),
          actualSum: parseInt(game.actualSum),
          won: game.won,
          betAmount: ethers.utils.formatEther(game.betAmount),
          netPayout: ethers.utils.formatEther(game.netPayout),
          timestamp: parseInt(game.timestamp) * 1000
        }));
        setGameHistory(formattedHistory);
      }

    } catch (error) {
      console.log('Using default game data');
      setGameStats({
        totalGames: 0,
        totalBets: '0',
        totalPayouts: '0',
        houseEdge: '95.0',
        playersOnline: Math.floor(Math.random() * 200) + 50
      });
    }
  };

  // Load BLB balance
  const loadBalance = async (tokenContract = contracts.blbToken, gameContract = contracts.luckyDice) => {
    try {
      if (!tokenContract || !gameContract || !walletData?.account) {
        setGameState(prev => ({
          ...prev,
          balance: '1000',
          allowance: '0'
        }));
        return;
      }

      let balance, allowance;

      try {
        balance = await tokenContract.balanceOf(walletData.account);
      } catch (e) {
        balance = ethers.utils.parseEther('1000');
      }

      try {
        allowance = await tokenContract.allowance(walletData.account, gameContract.address);
      } catch (e) {
        allowance = ethers.BigNumber.from(0);
      }

      setGameState(prev => ({
        ...prev,
        balance: ethers.utils.formatEther(balance),
        allowance: ethers.utils.formatEther(allowance)
      }));

    } catch (error) {
      console.log('Using demo balance');
      setGameState(prev => ({
        ...prev,
        balance: '1000',
        allowance: '0'
      }));
    }
  };

  // Admin Functions
  const handlePauseGame = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.luckyDice) {
        const tx = await contracts.luckyDice.pauseGame();
        await tx.wait();
        setSuccessMessage('✅ Game paused successfully!');
      } else {
        setSuccessMessage('✅ Game paused! (Demo)');
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to pause game: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUnpauseGame = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.luckyDice) {
        const tx = await contracts.luckyDice.unpauseGame();
        await tx.wait();
        setSuccessMessage('✅ Game unpaused successfully!');
      } else {
        setSuccessMessage('✅ Game unpaused! (Demo)');
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to unpause game: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleFundHouse = async () => {
    try {
      if (!adminForm.fundAmount || parseFloat(adminForm.fundAmount) <= 0) {
        setErrorMessage('Please enter a valid fund amount');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');
      const fundAmountWei = ethers.utils.parseEther(adminForm.fundAmount);

      if (contracts.luckyDice && contracts.blbToken) {
        const allowance = await contracts.blbToken.allowance(walletData.account, contracts.luckyDice.address);
        if (allowance.lt(fundAmountWei)) {
          const approveTx = await contracts.blbToken.approve(contracts.luckyDice.address, ethers.constants.MaxUint256);
          await approveTx.wait();
        }

        const tx = await contracts.luckyDice.fundHouse(fundAmountWei);
        await tx.wait();
        setSuccessMessage(`✅ House funded with ${adminForm.fundAmount} BLB!`);
      } else {
        setSuccessMessage(`✅ House funded with ${adminForm.fundAmount} BLB! (Demo)`);
      }

      setAdminForm(prev => ({ ...prev, fundAmount: '' }));
      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to fund house: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleWithdrawHouse = async () => {
    try {
      if (!adminForm.withdrawAmount || parseFloat(adminForm.withdrawAmount) <= 0) {
        setErrorMessage('Please enter a valid withdraw amount');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');
      const withdrawAmountWei = ethers.utils.parseEther(adminForm.withdrawAmount);

      if (contracts.luckyDice) {
        const tx = await contracts.luckyDice.withdrawHouse(withdrawAmountWei);
        await tx.wait();
        setSuccessMessage(`✅ Withdrew ${adminForm.withdrawAmount} BLB from house!`);
      } else {
        setSuccessMessage(`✅ Withdrew ${adminForm.withdrawAmount} BLB from house! (Demo)`);
      }

      setAdminForm(prev => ({ ...prev, withdrawAmount: '' }));
      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to withdraw from house: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleToggleDividend = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.luckyDice) {
        const tx = await contracts.luckyDice.toggleDividend(!adminForm.dividendEnabled);
        await tx.wait();
        setSuccessMessage(`✅ Dividend ${!adminForm.dividendEnabled ? 'enabled' : 'disabled'}!`);
      } else {
        setSuccessMessage(`✅ Dividend ${!adminForm.dividendEnabled ? 'enabled' : 'disabled'}! (Demo)`);
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to toggle dividend: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUpdateNoWinChance = async () => {
    try {
      const noWinChance = Math.round(parseFloat(adminForm.noWinChance) * 100);
      
      if (noWinChance < 0 || noWinChance > 5000) {
        setErrorMessage('No win chance must be between 0-50%');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.luckyDice) {
        const tx = await contracts.luckyDice.updateNoWinChance(noWinChance);
        await tx.wait();
        setSuccessMessage('✅ No win chance updated successfully!');
      } else {
        setSuccessMessage('✅ No win chance updated successfully! (Demo)');
      }

      await Promise.all([loadAdminData(), loadGameData()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to update no win chance: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleUpdateSumPayouts = async () => {
    try {
      const payouts = adminForm.sumPayouts.map(p => Math.round(parseFloat(p) * 100));
      
      // Validate payouts
      for (let i = 0; i < payouts.length; i++) {
        if (payouts[i] < 100 || payouts[i] > 5000) {
          setErrorMessage(`Payout for sum ${i + 2} must be between 1x-50x`);
          return;
        }
      }

      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.luckyDice) {
        const tx = await contracts.luckyDice.updateSumPayouts(payouts);
        await tx.wait();
        setSuccessMessage('✅ Sum payouts updated successfully!');
      } else {
        setSuccessMessage('✅ Sum payouts updated successfully! (Demo)');
      }

      await Promise.all([loadAdminData(), loadGameData()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to update payouts: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleSetBetLimits = async () => {
    try {
      const minBet = parseFloat(adminForm.minBet);
      const maxBet = parseFloat(adminForm.maxBet);

      if (minBet <= 0 || maxBet <= 0 || minBet >= maxBet) {
        setErrorMessage('Invalid bet limits');
        return;
      }

      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.luckyDice) {
        const minBetWei = ethers.utils.parseEther(adminForm.minBet);
        const maxBetWei = ethers.utils.parseEther(adminForm.maxBet);
        
        const tx = await contracts.luckyDice.setBetLimits(minBetWei, maxBetWei);
        await tx.wait();
        setSuccessMessage(`✅ Bet limits updated! Min: ${adminForm.minBet} BLB, Max: ${adminForm.maxBet} BLB`);
      } else {
        setSuccessMessage(`✅ Bet limits updated! Min: ${adminForm.minBet} BLB, Max: ${adminForm.maxBet} BLB (Demo)`);
      }

      await loadAdminData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to set bet limits: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  const handleEmergencyWithdraw = async () => {
    try {
      setAdminLoading(true);
      setErrorMessage('');

      if (contracts.luckyDice) {
        const tx = await contracts.luckyDice.emergencyWithdrawAll();
        await tx.wait();
        setSuccessMessage('✅ Emergency withdrawal completed!');
      } else {
        setSuccessMessage('✅ Emergency withdrawal completed! (Demo)');
      }

      await Promise.all([loadAdminData(), loadBalance()]);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrorMessage('Failed to emergency withdraw: ' + (error.reason || error.message));
    } finally {
      setAdminLoading(false);
    }
  };

  // Handle dice roll
  const handleRoll = async () => {
    if (!gameState.selectedSum) {
      setErrorMessage('Please select a sum to bet on');
      return;
    }

    try {
      setErrorMessage('');
      setSuccessMessage('');

      const betAmount = parseFloat(gameState.betAmount);
      const balance = parseFloat(gameState.balance);

      if (betAmount <= 0 || betAmount > balance) {
        setErrorMessage('Invalid bet amount');
        return;
      }

      playGameSound('epicStart', 0.5);
      
      setGameState(prev => ({ 
        ...prev, 
        isRolling: true, 
        showRollPopup: true 
      }));
      setAnimationState('rolling');

      // Start dice animations
      diceRefs.forEach((die, index) => {
        setTimeout(() => {
          if (die.current) {
            die.current.style.animation = `diceRoll 0.5s ease-in-out infinite`;
          }
        }, index * 200);
      });

      setTimeout(() => playGameSound('diceRoll', 0.6), 300);

      // If contracts available, use real blockchain
      if (contracts.luckyDice && contracts.blbToken) {
        try {
          const betAmountWei = ethers.utils.parseEther(gameState.betAmount);
          const tx = await contracts.luckyDice.rollDice(gameState.selectedSum, betAmountWei);
          
          setSuccessMessage('Transaction submitted! Rolling dice...');
          
          const receipt = await tx.wait();
          
          const gamePlayedEvent = receipt.events?.find(event => event.event === 'GamePlayed');
          
          if (gamePlayedEvent) {
            const [player, betAmount, betSum, dice1, dice2, actualSum, grossPayout, netPayout, won] = gamePlayedEvent.args;
            
            const gameResult = {
              player,
              betAmount: ethers.utils.formatEther(betAmount),
              betSum: parseInt(betSum),
              dice1: parseInt(dice1),
              dice2: parseInt(dice2),
              actualSum: parseInt(actualSum),
              grossPayout: ethers.utils.formatEther(grossPayout),
              netPayout: ethers.utils.formatEther(netPayout),
              won,
              timestamp: Date.now()
            };

            setTimeout(() => {
              handleRollResult(gameResult, true);
            }, 2000);
            
            await Promise.all([loadGameData(), loadBalance()]);
            return;
          }
        } catch (contractError) {
          console.error('Contract transaction failed:', contractError);
          
          setErrorMessage('Wallet connection low bandwidth. Refresh and try again');
          setAnimationState('idle');
          setGameState(prev => ({ 
            ...prev, 
            isRolling: false, 
            showRollPopup: false 
          }));
          diceRefs.forEach(die => {
            if (die.current) {
              die.current.style.animation = '';
            }
          });
          return;
        }
      }

      // If no contracts available, show connection error
      setErrorMessage('Smart contract not connected. Please refresh the page and connect your wallet.');
      setAnimationState('idle');
      setGameState(prev => ({ 
        ...prev, 
        isRolling: false, 
        showRollPopup: false 
      }));
      diceRefs.forEach(die => {
        if (die.current) {
          die.current.style.animation = '';
        }
      });

    } catch (error) {
      console.error('Game error:', error);
      setErrorMessage('Transaction failed. Please refresh and try again.');
      setAnimationState('idle');
      setGameState(prev => ({ 
        ...prev, 
        isRolling: false, 
        showRollPopup: false 
      }));
      diceRefs.forEach(die => {
        if (die.current) {
          die.current.style.animation = '';
        }
      });
    }
  };

  const handleRollResult = (rollResult, isReal) => {
    setRollResult(rollResult);
    setAnimationState('result');

    // Stop dice animations and show result
    diceRefs.forEach((die, index) => {
      setTimeout(() => {
        if (die.current) {
          die.current.style.animation = '';
        }
        playGameSound('diceStop', 0.3);
      }, index * 200);
    });

    // Update dice display
    setGameState(prev => ({
      ...prev,
      dice: [rollResult.dice1, rollResult.dice2]
    }));

    setTimeout(() => {
      if (rollResult.won) {
        const payout = parseFloat(rollResult.netPayout);
        if (payout >= 100) {
          playGameSound('epicWin', 0.8);
          setSuccessMessage(`⚔️ LEGENDARY WIN! You won ${formatNumber(rollResult.netPayout)} BLB! ⚔️`);
        } else if (payout >= 20) {
          playGameSound('bigWin', 0.6);
          setSuccessMessage(`🗡️ Great Victory! You won ${formatNumber(rollResult.netPayout)} BLB!`);
        } else {
          playGameSound('smallWin', 0.4);
          setSuccessMessage(`🏆 You won ${formatNumber(rollResult.netPayout)} BLB!`);
        }
      } else {
        playGameSound('lose', 0.3);
        setErrorMessage(`⚔️ The dice gods were not with you! Lost ${formatNumber(rollResult.betAmount)} BLB`);
      }
    }, 600);

    // Update local state for real games
    if (isReal) {
      setGameState(prev => ({
        ...prev,
        isRolling: false,
        totalWins: rollResult.won ? prev.totalWins + 1 : prev.totalWins,
        experience: prev.experience + (rollResult.won ? 150 : 30),
        playerLevel: Math.floor((prev.experience + (rollResult.won ? 150 : 30)) / 300) + 1
      }));

      setGameHistory(prev => [rollResult, ...prev.slice(0, 4)]);
    }

    // Reset after showing result
    setTimeout(() => {
      setAnimationState('idle');
      setRollResult(null);
      setGameState(prev => ({ 
        ...prev, 
        isRolling: false,
        showRollPopup: false,
        selectedSum: null
      }));
      setErrorMessage('');
      setSuccessMessage('');
    }, 4000);
  };

  // Handle token approval
  const handleApprove = async () => {
    try {
      setErrorMessage('');
      playGameSound('buttonClick', 0.3);
      setGameState(prev => ({ ...prev, isApproving: true }));

      if (contracts.blbToken && contracts.luckyDice) {
        const maxAmount = ethers.constants.MaxUint256;
        const tx = await contracts.blbToken.approve(contracts.luckyDice.address, maxAmount);
        await tx.wait();
        await loadBalance();
        setSuccessMessage('✅ Approval successful!');
      } else {
        setGameState(prev => ({ ...prev, allowance: '999999999' }));
        setSuccessMessage('✅ Approval successful!');
      }

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Approval error:', error);
      setErrorMessage('Approval failed: ' + error.message);
    } finally {
      setGameState(prev => ({ ...prev, isApproving: false }));
    }
  };

  const handleSumSelect = (sum) => {
    playGameSound('sumSelect', 0.3);
    setGameState(prev => ({ ...prev, selectedSum: sum }));
  };

  const needsApproval = () => {
    return parseFloat(gameState.allowance) < parseFloat(gameState.betAmount);
  };

  const canPlay = () => {
    return connected && 
           gameState.selectedSum !== null &&
           parseFloat(gameState.balance) >= parseFloat(gameState.betAmount) &&
           !needsApproval() &&
           !gameState.isRolling &&
           !adminData.isPaused;
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toFixed(2);
  };

  const getPayout = (sum) => {
    if (!adminData.gameConfig) return '2.0x';
    const index = sum - 2;
    const payout = adminData.gameConfig.sumPayouts[index] || 200;
    return (payout / 100).toFixed(1) + 'x';
  };

  if (!connected) {
    return (
      <div className="luckydice-gaming-arena">
        <div className="luckydice-connect-screen">
          <div className="luckydice-connect-icon">⚔️</div>
          <h3 className="luckydice-connect-title">🎲 ENTER THE ARENA</h3>
          <p className="luckydice-connect-desc">Connect your wallet to join the epic dice battles</p>
          <button 
            className="luckydice-action-btn luckydice-roll-btn"
            onClick={() => {
              playGameSound('buttonClick', 0.4);
              setConnected(true);
              setGameState(prev => ({ ...prev, balance: '1000' }));
            }}
            onMouseEnter={() => playGameSound('buttonHover', 0.2)}
            style={{ marginTop: '2rem', width: 'auto', padding: '1rem 2rem' }}
          >
            🔗 CONNECT WALLET
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="luckydice-gaming-arena">
      {/* Header */}
      <div className="luckydice-header-bar">
        <button 
          className="luckydice-back-btn" 
          onClick={() => {
            playGameSound('buttonClick', 0.3);
            if (onBack) onBack();
          }}
          onMouseEnter={() => playGameSound('buttonHover', 0.2)}
        >
          ← EXIT ARENA
        </button>
        
        <div className="luckydice-title-section">
          <h2 className="luckydice-main-title">LUCKY DICE</h2>
          <div className="luckydice-subtitle">
            Epic Dice Battles Await
          </div>
          {adminData.isPaused && (
            <div style={{ color: '#ff4500', fontSize: '0.9rem', fontWeight: '800', marginTop: '0.3rem' }}>
              ⚠️ ARENA CLOSED
            </div>
          )}
        </div>
        
        <div className="luckydice-balance-display">
          <div className="luckydice-balance-amount">
            💎 {formatNumber(gameState.balance)} BLB
          </div>
          <div className="luckydice-player-level">
            LVL {gameState.playerLevel}
          </div>
          
          {isAdmin && (
            <button
              className="luckydice-admin-btn"
              onClick={() => {
                setShowAdminPanel(!showAdminPanel);
                playGameSound('buttonClick', 0.3);
              }}
              title="Admin Panel"
            >
              🛡️
            </button>
          )}
          
          <button
            className="luckydice-sound-btn"
            onClick={() => {
              setSoundEnabled(!soundEnabled);
              playGameSound('buttonClick', 0.2);
            }}
            title={soundEnabled ? 'Disable Sound' : 'Enable Sound'}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
        </div>
      </div>

      {/* Admin Panel */}
      {isAdmin && showAdminPanel && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(13, 16, 23, 0.98) 0%, rgba(26, 15, 10, 0.95) 100%)',
            border: '3px solid #ff8c00',
            borderRadius: '20px',
            padding: '2rem',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <h2 style={{ color: '#ffd700', fontSize: '1.8rem', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '2px', margin: 0, fontFamily: 'MedievalSharp, cursive' }}>
                🛡️ ARENA MASTER PANEL
              </h2>
              <button
                onClick={() => setShowAdminPanel(false)}
                style={{
                  background: '#ff4500',
                  border: 'none',
                  color: 'white',
                  width: '2.5rem',
                  height: '2.5rem',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                  fontWeight: '800'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
              {/* Arena Status */}
              <div className="luckydice-control-panel">
                <label className="luckydice-control-label">⚔️ ARENA STATUS</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600', fontFamily: 'Cinzel, serif' }}>
                    Status: <span style={{ color: adminData.isPaused ? '#ff4500' : '#ffd700' }}>
                      {adminData.isPaused ? '⚠️ CLOSED' : '⚔️ OPEN FOR BATTLE'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button
                      className="luckydice-action-btn"
                      onClick={handlePauseGame}
                      disabled={adminLoading || adminData.isPaused}
                      style={{
                        flex: 1,
                        padding: '1rem',
                        fontSize: '0.9rem',
                        background: adminData.isPaused ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #ff4500, #8b0000)',
                        borderColor: '#ff4500'
                      }}
                    >
                      🛡️ CLOSE ARENA
                    </button>
                    <button
                      className="luckydice-action-btn"
                      onClick={handleUnpauseGame}
                      disabled={adminLoading || !adminData.isPaused}
                      style={{
                        flex: 1,
                        padding: '1rem',
                        fontSize: '0.9rem',
                        background: !adminData.isPaused ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #ffd700, #b8860b)',
                        borderColor: '#ffd700',
                        color: '#000'
                      }}
                    >
                      ⚔️ OPEN ARENA
                    </button>
                  </div>
                </div>
              </div>

              {/* House Treasury */}
              <div className="luckydice-control-panel">
                <label className="luckydice-control-label">🏰 HOUSE TREASURY</label>
                <div style={{ color: '#ffd700', fontSize: '1.5rem', fontWeight: '800', marginBottom: '1rem', fontFamily: 'Cinzel, serif' }}>
                  💰 {formatNumber(adminData.houseBalance)} BLB
                </div>
                <button
                  className="luckydice-action-btn"
                  onClick={handleEmergencyWithdraw}
                  disabled={adminLoading}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '0.9rem',
                    background: 'linear-gradient(135deg, #ff4500, #8b0000)',
                    borderColor: '#ff4500'
                  }}
                >
                  {adminLoading ? '⏳ PROCESSING...' : '🚨 EMERGENCY WITHDRAW ALL'}
                </button>
              </div>

              {/* Fund House */}
              <div className="luckydice-control-panel">
                <label className="luckydice-control-label">💰 FUND TREASURY</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    className="luckydice-stake-input"
                    type="number"
                    placeholder="Amount to fund treasury"
                    value={adminForm.fundAmount}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, fundAmount: e.target.value }))}
                    min="1"
                    step="1"
                    style={{ marginBottom: 0 }}
                  />
                  <button
                    className="luckydice-action-btn"
                    onClick={handleFundHouse}
                    disabled={adminLoading || !adminForm.fundAmount}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #ffd700, #b8860b)',
                      borderColor: '#ffd700',
                      color: '#000'
                    }}
                  >
                    {adminLoading ? '⏳ FUNDING...' : '💎 FUND TREASURY'}
                  </button>
                </div>
              </div>

              {/* Withdraw House */}
              <div className="luckydice-control-panel">
                <label className="luckydice-control-label">💸 WITHDRAW TREASURY</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <input
                    className="luckydice-stake-input"
                    type="number"
                    placeholder="Amount to withdraw"
                    value={adminForm.withdrawAmount}
                    onChange={(e) => setAdminForm(prev => ({ ...prev, withdrawAmount: e.target.value }))}
                    min="1"
                    step="1"
                    style={{ marginBottom: 0 }}
                  />
                  <button
                    className="luckydice-action-btn"
                    onClick={handleWithdrawHouse}
                    disabled={adminLoading || !adminForm.withdrawAmount}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #ff4500, #8b0000)',
                      borderColor: '#ff4500'
                    }}
                  >
                    {adminLoading ? '⏳ WITHDRAWING...' : '💸 WITHDRAW'}
                  </button>
                </div>
              </div>

              {/* Bet Limits */}
              <div className="luckydice-control-panel">
                <label className="luckydice-control-label">🎯 BET LIMITS</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem', fontFamily: 'Cinzel, serif' }}>MIN BET</label>
                      <input
                        className="luckydice-stake-input"
                        type="number"
                        value={adminForm.minBet}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, minBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem', fontFamily: 'Cinzel, serif' }}>MAX BET</label>
                      <input
                        className="luckydice-stake-input"
                        type="number"
                        value={adminForm.maxBet}
                        onChange={(e) => setAdminForm(prev => ({ ...prev, maxBet: e.target.value }))}
                        min="1"
                        step="1"
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                  </div>
                  <button
                    className="luckydice-action-btn"
                    onClick={handleSetBetLimits}
                    disabled={adminLoading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #ffd700, #b8860b)',
                      borderColor: '#ffd700',
                      color: '#000'
                    }}
                  >
                    {adminLoading ? '⏳ UPDATING...' : '🎯 SET LIMITS'}
                  </button>
                </div>
              </div>

              {/* Dividend System */}
              <div className="luckydice-control-panel">
                <label className="luckydice-control-label">💸 DIVIDEND SYSTEM</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ color: 'white', fontSize: '1rem', fontWeight: '600', fontFamily: 'Cinzel, serif' }}>
                    Status: <span style={{ color: adminForm.dividendEnabled ? '#ffd700' : '#ff4500' }}>
                      {adminForm.dividendEnabled ? '✅ ACTIVE' : '❌ INACTIVE'}
                    </span>
                  </div>
                  <button
                    className="luckydice-action-btn"
                    onClick={handleToggleDividend}
                    disabled={adminLoading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: adminForm.dividendEnabled 
                        ? 'linear-gradient(135deg, #ff4500, #8b0000)' 
                        : 'linear-gradient(135deg, #ffd700, #b8860b)',
                      borderColor: adminForm.dividendEnabled ? '#ff4500' : '#ffd700',
                      color: adminForm.dividendEnabled ? 'white' : '#000'
                    }}
                  >
                    {adminLoading ? '⏳ TOGGLING...' : (adminForm.dividendEnabled ? '❌ DISABLE DIVIDEND' : '✅ ENABLE DIVIDEND')}
                  </button>
                </div>
              </div>

              {/* House Edge Control */}
              <div className="luckydice-control-panel">
                <label className="luckydice-control-label">🏰 HOUSE EDGE</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem', fontFamily: 'Cinzel, serif' }}>
                      NO WIN CHANCE (%)
                    </label>
                    <input
                      className="luckydice-stake-input"
                      type="number"
                      value={adminForm.noWinChance}
                      onChange={(e) => setAdminForm(prev => ({ ...prev, noWinChance: e.target.value }))}
                      min="0"
                      max="50"
                      step="0.1"
                      style={{ marginBottom: 0 }}
                      placeholder="5.0"
                    />
                  </div>
                  <div style={{ 
                    color: 'rgba(255,255,255,0.7)', 
                    fontSize: '0.8rem',
                    padding: '0.8rem',
                    background: 'rgba(255, 140, 0, 0.1)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 140, 0, 0.3)',
                    fontFamily: 'Cinzel, serif'
                  }}>
                    <strong>Current House Edge:</strong> <span style={{ color: '#ffd700', fontWeight: '800' }}>{adminForm.noWinChance}%</span>
                    <br />
                    <small>This percentage ensures house always wins regardless of dice result</small>
                  </div>
                  <button
                    className="luckydice-action-btn"
                    onClick={handleUpdateNoWinChance}
                    disabled={adminLoading}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      fontSize: '0.9rem',
                      background: 'linear-gradient(135deg, #ff4500, #8b0000)',
                      borderColor: '#ff4500'
                    }}
                  >
                    {adminLoading ? '⏳ UPDATING...' : '🏰 UPDATE HOUSE EDGE'}
                  </button>
                </div>
              </div>

              {/* Sum Payouts Configuration */}
              <div className="luckydice-control-panel" style={{ gridColumn: '1 / -1' }}>
                <label className="luckydice-control-label">🎲 SUM PAYOUT CONFIGURATION</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
                  {adminForm.sumPayouts.map((payout, index) => (
                    <div key={index}>
                      <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem', fontFamily: 'Cinzel, serif' }}>
                        SUM {index + 2} (x)
                      </label>
                      <input
                        className="luckydice-stake-input"
                        type="number"
                        value={payout}
                        onChange={(e) => {
                          const newPayouts = [...adminForm.sumPayouts];
                          newPayouts[index] = e.target.value;
                          setAdminForm(prev => ({ ...prev, sumPayouts: newPayouts }));
                        }}
                        min="1"
                        max="50"
                        step="0.1"
                        style={{ marginBottom: 0, fontSize: '0.9rem', padding: '0.8rem' }}
                        placeholder={(index === 0 || index === 10) ? '30.0' : index === 5 ? '4.0' : '10.0'}
                      />
                    </div>
                  ))}
                </div>

                <div style={{ 
                  color: 'rgba(255,255,255,0.7)', 
                  fontSize: '0.8rem',
                  marginBottom: '1rem',
                  padding: '1rem',
                  background: 'rgba(255, 140, 0, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 140, 0, 0.2)',
                  fontFamily: 'Cinzel, serif'
                }}>
                  <strong>Payout Guide:</strong><br />
                  • Snake Eyes (2) & Boxcars (12): Rare rolls, high payouts (typically 30x)<br />
                  • Lucky Seven (7): Most common roll, lowest payout (typically 4x)<br />
                  • Other sums: Balanced payouts based on probability (6x-15x)
                </div>

                <button
                  className="luckydice-action-btn"
                  onClick={handleUpdateSumPayouts}
                  disabled={adminLoading}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '0.9rem',
                    background: 'linear-gradient(135deg, #ffd700, #b8860b)',
                    borderColor: '#ffd700',
                    color: '#000'
                  }}
                >
                  {adminLoading ? '⏳ UPDATING...' : '🎲 UPDATE PAYOUTS'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {errorMessage && (
        <div className="luckydice-message luckydice-error-msg">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="luckydice-message luckydice-success-msg">
          {successMessage}
        </div>
      )}

      <div className="luckydice-game-layout">
        {/* Main Game Zone */}
        <div className="luckydice-game-zone">
          {/* Dice Table */}
          <div className="luckydice-table-container">
            <div className="luckydice-dice-container">
              {gameState.dice.map((dieValue, index) => (
                <div 
                  key={index}
                  ref={diceRefs[index]}
                  className={`luckydice-die ${gameState.isRolling ? 'rolling' : ''}`}
                  onClick={!gameState.isRolling ? () => {
                    playGameSound('buttonClick', 0.2);
                    // Animate die on click
                    if (diceRefs[index].current) {
                      diceRefs[index].current.style.transform = 'scale(1.1) rotateY(180deg)';
                      setTimeout(() => {
                        diceRefs[index].current.style.transform = 'scale(1) rotateY(0deg)';
                      }, 300);
                    }
                  } : undefined}
                >
                  <div className="luckydice-die-value">
                    {animationState === 'result' && rollResult ? 
                      (index === 0 ? rollResult.dice1 : rollResult.dice2) : dieValue
                    }
                  </div>
                </div>
              ))}
            </div>

            {/* Victory Particles */}
            {particles.map(particle => (
              <div
                key={particle.id}
                style={{
                  position: 'absolute',
                  left: `${particle.x}%`,
                  top: `${particle.y}%`,
                  width: `${particle.size}px`,
                  height: `${particle.size}px`,
                  background: `radial-gradient(circle, 
                    hsl(${Math.random() * 60 + 30}, 100%, 70%), 
                    hsl(${Math.random() * 60 + 30}, 100%, 50%))`,
                  borderRadius: '50%',
                  opacity: particle.life / 100,
                  animation: 'particleFloat 4s ease-out',
                  pointerEvents: 'none',
                  zIndex: 5,
                  boxShadow: '0 0 10px currentColor'
                }}
              />
            ))}
          </div>

          {/* Betting Board */}
          <div className="luckydice-betting-board">
            {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(sum => (
              <div
                key={sum}
                className={`luckydice-bet-option ${gameState.selectedSum === sum ? 'selected' : ''}`}
                onClick={() => handleSumSelect(sum)}
                onMouseEnter={() => playGameSound('buttonHover', 0.1)}
              >
                <div className="luckydice-bet-number">{sum}</div>
                <div className="luckydice-bet-payout">{getPayout(sum)}</div>
                <div className="luckydice-bet-name">{sumNames[sum - 2]}</div>
              </div>
            ))}
          </div>

          {/* Roll Popup */}
          {gameState.showRollPopup && (
            <div className="luckydice-roll-popup-overlay">
              <div className="luckydice-roll-popup">
                <div className="luckydice-popup-header">
                  <h2>🎲 ROLLING THE DICE OF DESTINY!</h2>
                  <div className="luckydice-bet-info">
                    <span>Stake: {formatNumber(gameState.betAmount)} BLB</span>
                    <span>Betting on: Sum {gameState.selectedSum}</span>
                    <span>Payout: {getPayout(gameState.selectedSum || 7)}</span>
                  </div>
                </div>

                <div className="luckydice-popup-dice">
                  {gameState.dice.map((dieValue, index) => (
                    <div 
                      key={index}
                      className={`luckydice-popup-die ${
                        animationState === 'rolling' ? 'rolling-animation' : ''
                      }`}
                    >
                      {animationState === 'result' && rollResult 
                        ? (index === 0 ? rollResult.dice1 : rollResult.dice2)
                        : dieValue
                      }
                    </div>
                  ))}
                </div>

                {/* Result Display in Popup */}
                {animationState === 'result' && rollResult && (
                  <div className="luckydice-popup-result-display">
                    <div className={`luckydice-popup-result-content ${
                      rollResult.won ? 'luckydice-popup-victory' : 'luckydice-popup-defeat'
                    }`}>
                      <div className="luckydice-popup-result-icon">
                        {rollResult.won ? '⚔️' : '💀'}
                      </div>
                      <h3 className="luckydice-popup-result-title">
                        {rollResult.won 
                          ? '🏆 EPIC VICTORY!'
                          : '⚔️ HONORABLE DEFEAT'
                        }
                      </h3>
                      <div className="luckydice-popup-result-amount">
                        {rollResult.won ? `+${formatNumber(rollResult.netPayout)}` : `-${formatNumber(rollResult.betAmount)}`} BLB
                      </div>
                      <div className="luckydice-popup-dice-result">
                        Dice: {rollResult.dice1} + {rollResult.dice2} = {rollResult.actualSum}
                      </div>
                      <div className="luckydice-popup-result-desc">
                        {rollResult.won 
                          ? `You predicted ${rollResult.betSum} and rolled ${rollResult.actualSum}!`
                          : rollResult.actualSum === rollResult.betSum
                            ? 'The dice gods were not with you!'
                            : `You predicted ${rollResult.betSum} but rolled ${rollResult.actualSum}`
                        }
                      </div>
                    </div>
                  </div>
                )}

                {/* Rolling indicator */}
                {animationState === 'rolling' && (
                  <div className="luckydice-rolling-indicator">
                    <div className="luckydice-rolling-text">⚡ DICE ROLLING...</div>
                    <div className="luckydice-rolling-subtext">May fortune favor the brave!</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Game Controls */}
          <div className="luckydice-controls">
            <div className="luckydice-control-panel">
              <label className="luckydice-control-label">💰 STAKE</label>
              <div>
                <input
                  className="luckydice-stake-input"
                  type="number"
                  value={gameState.betAmount}
                  onChange={(e) => setGameState(prev => ({ ...prev, betAmount: e.target.value }))}
                  disabled={gameState.isRolling || adminData.isPaused}
                  min={adminData.minBet}
                  max={Math.min(parseFloat(gameState.balance), parseFloat(adminData.maxBet))}
                  step="1"
                  onFocus={() => playGameSound('buttonClick', 0.1)}
                />
                <div className="luckydice-preset-buttons">
                  {['1', '10', '100'].map(amount => (
                    <button
                      key={amount}
                      onClick={() => {
                        playGameSound('buttonClick', 0.2);
                        setGameState(prev => ({ ...prev, betAmount: amount }));
                      }}
                      onMouseEnter={() => playGameSound('buttonHover', 0.1)}
                      disabled={gameState.isRolling || adminData.isPaused}
                      className="luckydice-preset-btn"
                    >
                      {amount}
                    </button>
                  ))}
                  <button
                    onClick={() => {
                      playGameSound('buttonClick', 0.2);
                      const maxAmount = Math.min(parseFloat(gameState.balance), parseFloat(adminData.maxBet));
                      setGameState(prev => ({ ...prev, betAmount: Math.floor(maxAmount).toString() }));
                    }}
                    onMouseEnter={() => playGameSound('buttonHover', 0.1)}
                    disabled={gameState.isRolling || adminData.isPaused}
                    className="luckydice-preset-btn max-btn"
                  >
                    MAX
                  </button>
                </div>
              </div>
            </div>

            <div className="luckydice-control-panel">
              {adminData.isPaused ? (
                <button
                  className="luckydice-action-btn"
                  disabled={true}
                  style={{
                    background: 'rgba(255, 69, 0, 0.3)',
                    color: '#ff4500',
                    borderColor: '#ff4500',
                    cursor: 'not-allowed'
                  }}
                >
                  ⚠️ ARENA CLOSED
                </button>
              ) : needsApproval() ? (
                <button
                  className="luckydice-action-btn luckydice-approve-btn"
                  onClick={handleApprove}
                  onMouseEnter={() => playGameSound('buttonHover', 0.2)}
                  disabled={gameState.isApproving}
                >
                  {gameState.isApproving ? '⏳ APPROVING...' : '🔓 APPROVE BLB'}
                </button>
              ) : (
                <button
                  className="luckydice-action-btn luckydice-roll-btn"
                  onClick={handleRoll}
                  onMouseEnter={() => playGameSound('buttonHover', 0.2)}
                  disabled={!canPlay()}
                >
                  {gameState.isRolling ? '🌪️ ROLLING...' : '🎲 ROLL DICE'}
                </button>
              )}
            </div>
          </div>

          {/* Potential Win Display */}
          {gameState.selectedSum && !gameState.isRolling && !adminData.isPaused && (
            <div className="luckydice-potential-win">
              ⚔️ Betting on Sum {gameState.selectedSum} • Win {formatNumber((parseFloat(gameState.betAmount) * parseFloat(getPayout(gameState.selectedSum).replace('x', ''))).toString())} BLB
            </div>
          )}
        </div>

        {/* Stats Sidebar */}
        <div className="luckydice-stats-sidebar">
          <div className="luckydice-stats-panel">
            <h3 className="luckydice-stats-title">🏆 WARRIOR STATUS</h3>
            <div className="luckydice-stats-item">
              <span className="luckydice-stats-label">Level</span>
              <span className="luckydice-stats-value">{gameState.playerLevel}</span>
            </div>
            <div className="luckydice-stats-item">
              <span className="luckydice-stats-label">XP</span>
              <span className="luckydice-stats-value">{formatNumber(gameState.experience)}</span>
            </div>
            <div className="luckydice-stats-item">
              <span className="luckydice-stats-label">Total Victories</span>
              <span className="luckydice-stats-value">{gameState.totalWins}</span>
            </div>
            <div className="luckydice-stats-item">
              <span className="luckydice-stats-label">Rank</span>
              <span className="luckydice-stats-value">Battle Master</span>
            </div>
          </div>

          <div className="luckydice-stats-panel">
            <h3 className="luckydice-stats-title">🎲 DICE WISDOM</h3>
            <div className="luckydice-stats-item">
              <span className="luckydice-stats-label">Most Likely</span>
              <span className="luckydice-stats-value">7 (16.67%)</span>
            </div>
            <div className="luckydice-stats-item">
              <span className="luckydice-stats-label">Rarest Rolls</span>
              <span className="luckydice-stats-value">2 & 12 (2.78%)</span>
            </div>
            <div className="luckydice-stats-item">
              <span className="luckydice-stats-label">Best Payout</span>
              <span className="luckydice-stats-value">Snake Eyes</span>
            </div>
            <div className="luckydice-stats-item">
              <span className="luckydice-stats-label">Strategy</span>
              <span className="luckydice-stats-value">Risk vs Reward</span>
            </div>
          </div>

          

          {gameHistory.length > 0 && (
            <div className="luckydice-stats-panel">
              <h3 className="luckydice-stats-title">📜 BATTLE HISTORY</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', maxHeight: '300px', overflowY: 'auto' }}>
                {gameHistory.map((battle, index) => (
                  <div key={index} className="luckydice-history-item">
                    <div className="luckydice-history-dice">
                      <div className="luckydice-history-die">{battle.dice1}</div>
                      <div className="luckydice-history-die">{battle.dice2}</div>
                    </div>
                    <div className="luckydice-history-result">
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <div className="luckydice-history-bet">
                          Bet {battle.betSum} • {formatNumber(battle.betAmount)} BLB
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', fontFamily: 'Cinzel, serif' }}>
                          Sum: {battle.actualSum}
                        </div>
                      </div>
                      <div className={`luckydice-history-payout ${
                        battle.won ? 'luckydice-history-win' : 'luckydice-history-loss'
                      }`}>
                        {battle.won ? `+${formatNumber(battle.netPayout)}` : 'DEFEAT'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LuckyDice;